import React, { lazy, Suspense, use, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm, useStore } from "@tanstack/react-form";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { ErrorMessage, Field, FieldGroup, Fieldset, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { ApiUser } from "src/api/api_user";
import { ApiPublic } from "src/api/api_public";
import { loadPasswordStrengthChecker, MIN_PASSWORD_SCORE } from "src/components/base/pw-strength";

const PasswordStrength = lazy(() => import("src/components/base/pw-strength"));

/**
 * The properties for {@link CreatePasswordDialog}
 */
export type CreatePasswordDialogProps = {
    /** Callback function when the dialog is submitted */
    onSubmit: () => void;
    /** Callback function when the dialog is closed */
    onClose: () => void;
    /** Which type of api call to use */
    endpoint: "public" | "user";
    /** Whether the dialog is open */
    isOpen: boolean;
};

/**
 * Dialog for creating password
 */
export default function CreatePasswordDialog(props: CreatePasswordDialogProps) {
    const [t] = useTranslation("authentication-dialog");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: {
            password: "",
            password2: "",
            showPassword: false,
        },
        validators: {
            onSubmitAsync: async ({ value: { password } }) => {
                const res =
                    props.endpoint === "public"
                        ? await ApiPublic.credentials.setPassword({ password })
                        : await ApiUser.credentials.createPassword({ password });

                if (res.result === "Err") {
                    return {
                        field: {
                            password: res.error.weak_password ? t("error.stronger-password") : undefined,
                        },
                    };
                }
            },
        },
        onSubmit: () => {
            props.onSubmit();
        },
    });

    const showPassword = useStore(form.store, (state) => state.values.showPassword);

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.endpoint]);

    const passwordStrengthChecker = use(loadPasswordStrengthChecker());

    return (
        <Dialog onClose={props.onClose} open={props.isOpen}>
            <DialogTitle>{t("heading.create-password")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field
                                name={"password"}
                                validators={{
                                    onChange: ({ value }) => {
                                        if (
                                            value !== "" &&
                                            passwordStrengthChecker.check(value).score < MIN_PASSWORD_SCORE
                                        ) {
                                            return t("error.stronger-password");
                                        }
                                        return undefined;
                                    },
                                }}
                            >
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.new-password")}</RequiredLabel>
                                        <div className={"flex gap-3"}>
                                            <Input
                                                autoComplete={"new-password"}
                                                autoFocus={true}
                                                required={true}
                                                type={showPassword ? "text" : "password"}
                                                invalid={fieldApi.state.meta.errors.length !== 0}
                                                value={fieldApi.state.value}
                                                onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            />
                                            <form.Field name={"showPassword"}>
                                                {(field) => (
                                                    <Button
                                                        plain={true}
                                                        onClick={() => field.handleChange((prev) => !prev)}
                                                    >
                                                        {field.state.value ? <EyeSlashIcon /> : <EyeIcon />}
                                                    </Button>
                                                )}
                                            </form.Field>
                                        </div>

                                        {fieldApi.state.meta.errors.map((error) => (
                                            <ErrorMessage className={"mt-3"}>{error}</ErrorMessage>
                                        ))}

                                        <Suspense fallback={<div></div>}>
                                            <PasswordStrength password={fieldApi.state.value} />
                                        </Suspense>
                                    </Field>
                                )}
                            </form.Field>
                            <form.Field
                                name={"password2"}
                                validators={{
                                    onChangeListenTo: ["password"],
                                    onChange: ({ value, fieldApi }) => {
                                        if (
                                            fieldApi.getMeta().isDirty &&
                                            value !== fieldApi.form.getFieldValue("password")
                                        ) {
                                            return t("text.password-mismatch");
                                        }
                                        return undefined;
                                    },
                                }}
                            >
                                {(field) => (
                                    <Field>
                                        <RequiredLabel>{t("label.reenter-password")}</RequiredLabel>
                                        <Input
                                            autoComplete={"new-password"}
                                            required={true}
                                            type={"password"}
                                            value={field.state.value}
                                            invalid={field.state.meta.errors.length !== 0}
                                            onBlur={field.handleBlur}
                                            onChange={(e) => field.handleChange(e.target.value)}
                                        />
                                        {field.state.meta.errors.map((error) => (
                                            <ErrorMessage>{error}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <DialogActions>
                                <PrimaryButton type="submit"> {tg("button.save")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
